import { cancelRequest, CategoriesApi } from 'api';
import axios from 'axios';
import debounce from 'lodash/debounce';
import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { APP_URL } from 'shared/constants/common';
import { SearchEvents, setDataLayer } from 'shared/constants/datalayer';
import { removeMultipleDash, removeSpecialCharacter } from 'shared/util/utility';

const useSearchBar = () => {
	const [showSearch, setShowSearch] = useState<boolean>(false);
	const [searchValue, setSearchValue] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(true);
	const [searchList, setSearchList] = useState([]);
	const [isSearchStarted, setIsSearchStarted] = useState<boolean>(false);

	const eventUrl = (detail: any) => {
		const { area, extras, name, id, categoryLevel } = detail;
		const { CATEGORY_NAME_1, CATEGORY_NAME_2, CATEGORY_NAME_3 } = extras;
		const categoryNames = [CATEGORY_NAME_1, CATEGORY_NAME_2, CATEGORY_NAME_3].map(removeSpecialCharacter);
		let URL = '';

		if (categoryLevel === 1 && area === 'LIVE_EVENT') {
			URL = `${APP_URL.live}?id=${id}`;
		} else if (categoryLevel === 1) {
			URL = `${APP_URL.sport}/${categoryNames[0]}`;
		} else if (categoryLevel === 2) {
			URL = `${APP_URL.sport}/${categoryNames.join('/')}${id}`;
		} else if (area === 'PREMATCH_EVENT') {
			URL = `${APP_URL.sport}/${categoryNames.join('/')}/${removeSpecialCharacter(name)}/${id}`;
		} else {
			const liveCategoryName = removeSpecialCharacter(name.replace('live', ''));
			URL = `${APP_URL.live}/${categoryNames[2]}/${liveCategoryName}-live/${id}`;
		}

		return removeMultipleDash(URL);
	};

	const submitSearch = async (value: string) => {
		cancelRequest('/market/events/quick-search', 'Not needed now');
		setLoading(true);
		const trimmedValue = value?.trim();
		if (!trimmedValue || trimmedValue.length < 3) {
			setSearchList([]);
			return;
		}
		const params = {
			languageCode: 'pl',
			mergeLanguages: true,
			pattern: trimmedValue,
		};
		try {
			const response = await CategoriesApi.getSearchResult(params);
			setSearchList(response.data.data.searchResult);
			setLoading(false);
		} catch (error) {
			if (axios.isCancel(error)) {
				return;
			}
			setLoading(false);
			setSearchList([]);
		}
	};

	const delayedQuery = useMemo(() => debounce(submitSearch, 400), []);

	const closeSearchModal = () => {
		setSearchValue('');
		setSearchList([]);
		cancelRequest('/market/events/quick-search', 'Canceled on unmount');
		setShowSearch(false);
		setIsSearchStarted(false);
	};

	useEffect(() => {
		delayedQuery(searchValue);
		if (!isSearchStarted && searchValue.length > 0) {
			setDataLayer(SearchEvents.DATALAYER_SEARCH_START);
			setIsSearchStarted(true);
		}

		return () => {
			delayedQuery.cancel();
		};
	}, [searchValue, delayedQuery]);

	const handleSearchInput = (e: ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		setSearchValue(value);
	};

	return {
		showSearch,
		searchValue,
		loading,
		searchList,
		eventUrl,
		setSearchValue,
		closeSearchModal,
		handleSearchInput,
		setShowSearch,
	};
};

export default useSearchBar;
